// Contabilidad › Analítico › INTELIGENCIA DE NEGOCIOS.
// Constructor de reportes financieros sobre balance y estado de resultados.
// FILAS = "cuentas virtuales" con nombre propio y una lista de términos con
// signo. COLUMNAS = tiempo (anual…semanal) o dimensión (centro de costo,
// proyecto, partner), nunca cruzadas.
//   · Pestaña "Reporte": ejecuta y pinta la matriz, con vistas calculadas
//     (valores, % vertical, variación vs. período anterior, Real vs.
//     Presupuesto), gráfico, drill-down al detalle de cada celda y exportación.
//   · Pestaña "Diseño" (BIEditor): editor de filas + panel de cobertura.
import { useMemo, useState } from 'react';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import {
  BarChart, Bar, CartesianGrid, Cell, Legend, Line, LineChart, ResponsiveContainer,
  Tooltip, XAxis, YAxis,
} from 'recharts';
import {
  BarChart3, ChevronDown, ChevronUp, Copy, Loader2, Pencil, Plus, RefreshCw, Trash2,
} from 'lucide-react';
import { toast } from 'sonner';
import { useTabsStore } from '@/stores/tabs.store';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { getErrorMessage } from '@/lib/api/client';
import { useDialogoEnLinea } from '@/components/ui/PanelIntegrado';
import { ExportarBotones, useMarcaEmpresa, type Celda } from '@/lib/utils/exportarTabla';
import {
  DimOpt, ESCALAS, centrosList, proyectosList, field, notaDeEscala, numFmt, usd, hoy, rpc,
} from './shared';
import { BIEditor, Borrador, aBorrador, borradorVacio } from './BIEditor';
import { BIDetalleModal } from './BIDetalle';
import { LOCALE } from '@/lib/utils/regional';

interface ReporteItem {
  id: string; nombre: string; descripcion: string | null; base: string;
  columnas_tipo: string; columnas_param: string; es_plantilla: boolean; filas: number;
}
interface Columna { clave: string; titulo: string }
interface Fila {
  orden: number; nivel: number; clase: string; etiqueta: string;
  medida: string; formato: string; negrita: boolean;
  valores: number[]; presupuesto: number[] | null;
}
interface Matriz {
  reporte: {
    id: string; nombre: string; descripcion: string | null; base: string;
    columnas_tipo: string; columnas_param: string; es_plantilla: boolean;
    mostrar_total?: boolean; mostrar_variacion?: boolean; fila_base_orden?: number | null;
  };
  desde: string; hasta: string; con_presupuesto?: boolean;
  columnas: Columna[]; filas: Fila[];
}

const TIPOS_COL: { v: string; label: string }[] = [
  { v: 'tiempo', label: 'Tiempo' },
  { v: 'centro', label: 'Centro de costo' },
  { v: 'proyecto', label: 'Proyecto' },
  { v: 'partner', label: 'Partner' },
];
const PERIODOS: { v: string; label: string }[] = [
  { v: 'anual', label: 'Anual' },
  { v: 'semestral', label: 'Semestral' },
  { v: 'trimestral', label: 'Trimestral' },
  { v: 'bimensual', label: 'Bimensual' },
  { v: 'mensual', label: 'Mensual' },
  { v: 'semanal', label: 'Semanal (operativo)' },
  { v: 'ninguno', label: 'Un solo total' },
];
type Vista = 'valores' | 'vertical' | 'variacion' | 'presupuesto';

type SubPresu = 'real' | 'ppto' | 'varia' | 'pct' | 'cumpl';
// `esPct` marca las subcolumnas que son porcentajes: ni se escalan ni se
// formatean como moneda. Son DOS lecturas distintas del mismo par de cifras:
//   · Var. %  = (real − ppto) / |ppto|  → 0 % es clavarlo; +10 % es pasarse.
//   · % ejec. = real / ppto             → 100 % es clavarlo; 90 % es quedarse corto.
const SUBS_PRESU: { k: SubPresu; label: string; esPct?: boolean; ayuda: string }[] = [
  { k: 'real', label: 'Real', ayuda: 'Lo que realmente pasó en el período' },
  { k: 'ppto', label: 'Ppto.', ayuda: 'Lo presupuestado para ese período' },
  { k: 'varia', label: 'Var.', ayuda: 'Real menos presupuesto, en dinero' },
  { k: 'pct', label: 'Var. %', esPct: true, ayuda: 'Cuánto te desviaste: 0 % es clavar el presupuesto' },
  { k: 'cumpl', label: '% ejec.', esPct: true, ayuda: 'Cuánto ejecutaste: 100 % es clavar el presupuesto' },
];

const inicioAnio = hoy.slice(0, 4) + '-01-01';

// Los porcentajes NUNCA se escalan. La escala en sí (y el criterio de quitar el
// símbolo de moneda al escalar) vive en ./shared para que el Dashboard use lo mismo.
const fmt = (v: number | null, formato: string, escala = 1) => {
  if (v == null) return '';
  if (formato === 'porcentaje') return `${v.toFixed(1)} %`;
  if (escala !== 1) return numFmt(v / escala);
  return formato === 'numero' ? numFmt(v) : usd(v);
};

const claseFila = (f: Fila) => {
  const base = f.negrita ? 'font-semibold' : '';
  switch (f.clase) {
    case 'total': return `${base} bg-muted/60 border-y`;
    case 'grupo': return `${base} font-medium border-t`;
    case 'subgrupo': return `${base} font-medium text-muted-foreground`;
    default: return base;
  }
};

function DimSelect({ label, lista, value, onChange }: {
  label: string; lista: DimOpt[]; value: string; onChange: (v: string) => void;
}) {
  return (
    <label className="flex flex-col text-xs text-muted-foreground">{label}
      <select className={`${field} mt-1 w-48`} value={value} onChange={(e) => onChange(e.target.value)}>
        <option value="">Todos</option>
        {lista.map((d) => <option key={d.id} value={d.id}>{d.codigo} · {d.nombre}</option>)}
      </select>
    </label>
  );
}

export function BI() {
  const qc = useQueryClient();
  const dlg = useDialogoEnLinea();
  const [reporteId, setReporteId] = useState('');
  const [desde, setDesde] = useState(inicioAnio);
  const [hasta, setHasta] = useState(hoy);
  const [colsTipo, setColsTipo] = useState('');
  const [colsParam, setColsParam] = useState('');
  const [centro, setCentro] = useState('');
  const [proyecto, setProyecto] = useState('');
  const [conTotal, setConTotal] = useState(true);
  const [vista, setVista] = useState<Vista>('valores');
  const [borrador, setBorrador] = useState<Borrador | null>(null);
  const [grafico, setGrafico] = useState(false);
  const [filaGrafico, setFilaGrafico] = useState<number | null>(null);
  const [celda, setCelda] = useState<{ filaOrden: number; colClave: string } | null>(null);
  const [escala, setEscala] = useState(1);
  // el % va apagado por defecto: con 12 meses las cuatro subcolumnas son 49 columnas
  const [subPresu, setSubPresu] = useState<Record<SubPresu, boolean>>(
    { real: true, ppto: true, varia: true, pct: false, cumpl: false });
  const marca = useMarcaEmpresa();
  const openTab = useTabsStore((st) => st.openTab);

  const { data: reportes = [], isLoading: cargandoLista } = useQuery<ReporteItem[]>({
    queryKey: ['bi-reportes'], queryFn: () => rpc<ReporteItem[]>('cont_bi_reportes'),
  });
  const { data: centros = [] } = useQuery<DimOpt[]>({ queryKey: ['cont-centros'], queryFn: centrosList });
  const { data: proyectos = [] } = useQuery<DimOpt[]>({ queryKey: ['cont-proyectos'], queryFn: proyectosList });

  const actual = reportes.find((r) => r.id === reporteId) ?? reportes[0];
  const tipoEfectivo = colsTipo || actual?.columnas_tipo || 'tiempo';
  const paramEfectivo = colsParam || actual?.columnas_param || 'mensual';
  const pidePresupuesto = vista === 'presupuesto' && tipoEfectivo === 'tiempo';

  const sembrar = useMutation({
    mutationFn: () => rpc<{ creadas: number }>('cont_bi_plantillas_sembrar'),
    onSuccess: (r) => {
      toast.success(r.creadas > 0 ? `${r.creadas} plantilla(s) restaurada(s)` : 'Las plantillas ya estaban creadas');
      void qc.invalidateQueries({ queryKey: ['bi-reportes'] });
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const abrirEditor = useMutation({
    mutationFn: (id: string) => rpc<Parameters<typeof aBorrador>[0]>('cont_bi_reporte', { _id: id }),
    onSuccess: (d) => setBorrador(aBorrador(d)),
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const clonar = useMutation({
    mutationFn: (id: string) => rpc<{ id: string; nombre: string }>('cont_bi_clonar', { _id: id }),
    onSuccess: async (r) => {
      toast.success(`Copia creada: ${r.nombre}`);
      await qc.invalidateQueries({ queryKey: ['bi-reportes'] });
      setReporteId(r.id);
      abrirEditor.mutate(r.id);
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const eliminar = useMutation({
    mutationFn: (id: string) => rpc('cont_bi_eliminar', { _id: id }),
    onSuccess: () => {
      toast.success('Reporte eliminado');
      setReporteId('');
      void qc.invalidateQueries({ queryKey: ['bi-reportes'] });
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const { data: matriz, isFetching, error } = useQuery<Matriz>({
    queryKey: ['bi-ejecutar', actual?.id, desde, hasta, tipoEfectivo, paramEfectivo, centro, proyecto, pidePresupuesto],
    queryFn: () => rpc<Matriz>('cont_bi_ejecutar', {
      _reporte: actual!.id, _desde: desde, _hasta: hasta,
      _cols_tipo: tipoEfectivo, _cols_param: paramEfectivo,
      _centro: centro || null, _proyecto: proyecto || null,
      _con_presupuesto: pidePresupuesto,
    }),
    enabled: !!actual?.id && !borrador,
  });

  const cols = useMemo(() => matriz?.columnas ?? [], [matriz]);
  const filas = useMemo(() => matriz?.filas ?? [], [matriz]);

  const baseOrden = matriz?.reporte?.fila_base_orden ?? null;
  const filaBase = useMemo(
    () => (baseOrden != null ? filas.find((f) => f.orden === baseOrden) : undefined),
    [filas, baseOrden],
  );
  const vistaEfectiva: Vista =
    vista === 'vertical' && !filaBase ? 'valores'
      : (vista === 'variacion' || vista === 'presupuesto') && tipoEfectivo !== 'tiempo' ? 'valores' : vista;

  const totalDe = (f: Fila) =>
    f.medida === 'saldo' ? (f.valores[f.valores.length - 1] ?? 0) : f.valores.reduce((a, b) => a + b, 0);
  const totalPresu = (f: Fila) =>
    f.presupuesto == null ? null
      : f.medida === 'saldo' ? (f.presupuesto[f.presupuesto.length - 1] ?? 0)
        : f.presupuesto.reduce((a, b) => a + b, 0);

  const valoresDe = useMemo(() => (f: Fila): (number | null)[] => {
    if (vistaEfectiva === 'variacion') return f.valores.map((v, i) => (i === 0 ? null : v - f.valores[i - 1]));
    if (vistaEfectiva === 'vertical' && filaBase) {
      return f.valores.map((v, i) => {
        const b = filaBase.valores[i];
        return b ? Math.round((v / b) * 1000) / 10 : null;
      });
    }
    return f.valores;
  }, [vistaEfectiva, filaBase]);

  const formatoDe = (f: Fila) => (vistaEfectiva === 'vertical' ? 'porcentaje' : f.formato);
  const totalVisible = conTotal && vistaEfectiva === 'valores';

  // --- Real vs. Presupuesto: se calcula EN CADA PERÍODO ------------------------
  // Las otras tres vistas ya eran columna a columna; esta colapsaba todo el rango
  // en cuatro columnas. Ahora cada período trae sus subcolumnas y al final va el
  // bloque del rango completo. Las subcolumnas se eligen para no desbordar el
  // ancho: con 12 meses y las cuatro serían 49 columnas.
  const esPresu = vistaEfectiva === 'presupuesto' && !!matriz?.con_presupuesto;
  // Sin presupuesto cargado el motor devuelve ceros, no nulos. Mostrar "0,00" y
  // una variación igual al real se confunde con "presupuesté cero", así que se
  // dice lo que pasa de verdad.
  const hayPresupuesto = filas.some((f) => f.presupuesto?.some((v) => v !== 0));

  const subs = useMemo(() => {
    const sel = SUBS_PRESU.filter((x) => subPresu[x.k]);
    return sel.length ? sel : [SUBS_PRESU[0]];   // al menos el Real
  }, [subPresu]);

  /** Real, presupuesto, variación y % de una fila. `i = null` → todo el rango. */
  const celdasPresu = (f: Fila, i: number | null) => {
    const real = i === null ? totalDe(f) : (f.valores[i] ?? 0);
    const ppto = i === null ? totalPresu(f) : (f.presupuesto?.[i] ?? null);
    return {
      real, ppto,
      varia: ppto == null ? null : real - ppto,
      pct: ppto ? ((real - ppto) / Math.abs(ppto)) * 100 : null,
      cumpl: ppto ? (real / ppto) * 100 : null,
    } as Record<SubPresu, number | null>;
  };

  const headers = useMemo(() => (
    esPresu
      ? ['Concepto',
         ...cols.flatMap((c) => subs.map((x) => `${c.titulo} · ${x.label}`)),
         ...subs.map((x) => `Total · ${x.label}`)]
      : ['Concepto', ...cols.map((c) => c.titulo), ...(totalVisible ? ['Total'] : [])]
  ), [cols, totalVisible, esPresu, subs]);

  // Se exporta lo MISMO que se ve, escala incluida; el subtítulo dice la unidad.
  const esc = (v: number | null) => (v == null ? null : Math.round((v / escala) * 100) / 100);
  // los porcentajes no se escalan nunca
  const escSub = (k: SubPresu, v: number | null) =>
    (SUBS_PRESU.find((x) => x.k === k)?.esPct
      ? (v == null ? null : Math.round(v * 10) / 10)
      : esc(v));
  const filasExport = useMemo<Celda[][]>(() => filas.map((f) => {
    if (esPresu) {
      return [
        '   '.repeat(f.nivel) + f.etiqueta,
        ...cols.flatMap((_, i) => { const c = celdasPresu(f, i); return subs.map((x) => escSub(x.k, c[x.k]) as Celda); }),
        ...(() => { const c = celdasPresu(f, null); return subs.map((x) => escSub(x.k, c[x.k]) as Celda); })(),
      ];
    }
    return [
      '   '.repeat(f.nivel) + f.etiqueta,
      // en la vista vertical los valores YA son porcentajes: no se escalan
      ...valoresDe(f).map((v) => (vistaEfectiva === 'vertical' ? v : esc(v)) as Celda),
      ...(totalVisible ? [esc(totalDe(f)) as Celda] : []),
    ];
  }), [filas, totalVisible, valoresDe, esPresu, vistaEfectiva, escala, cols, subs]);   // eslint-disable-line react-hooks/exhaustive-deps

  const colsDerecha = useMemo(
    () => Array.from({ length: headers.length - 1 }, (_, i) => i + 1),
    [headers.length],
  );

  const etiquetaVista = vistaEfectiva === 'vertical'
    ? ` · % sobre "${filaBase?.etiqueta ?? ''}"`
    : vistaEfectiva === 'variacion' ? ' · variación vs. período anterior'
      : esPresu ? ' · Real vs. Presupuesto' : '';
  const notaEscala = notaDeEscala(escala);
  const subtitulo = actual
    ? `${desde} a ${hasta} · columnas: ${TIPOS_COL.find((t) => t.v === tipoEfectivo)?.label ?? tipoEfectivo}`
      + (tipoEfectivo === 'tiempo' ? ` (${PERIODOS.find((p) => p.v === paramEfectivo)?.label ?? paramEfectivo})` : '')
      + etiquetaVista
      + (notaEscala ? ` · ${notaEscala}` : '')
    : '';

  // --- gráfico ---------------------------------------------------------------
  const filaGraf = filas.find((f) => f.orden === filaGrafico)
    ?? filas.filter((f) => f.clase === 'total').slice(-1)[0] ?? filas[0];
  const datosGraf = useMemo(
    () => cols.map((c, i) => ({
      periodo: c.titulo,
      valor: (filaGraf?.valores[i] ?? 0) / escala,
      presupuesto: filaGraf?.presupuesto?.[i] == null ? null : filaGraf.presupuesto[i] / escala,
    })),
    [cols, filaGraf, escala],
  );
  const esSaldo = filaGraf?.medida === 'saldo';

  // ------------------------------------------------------------- DISEÑO ------
  if (borrador) {
    return (
      <div className="space-y-4 p-6">
        <div className="flex items-center gap-2">
          <BarChart3 className="h-5 w-5 text-primary" />
          <h1 className="text-xl font-semibold">{borrador.id ? 'Editar reporte' : 'Nuevo reporte'}</h1>
        </div>
        <BIEditor
          borrador={borrador} setBorrador={setBorrador} desde={desde} hasta={hasta}
          onGuardado={(id) => { setReporteId(id); setBorrador(null); }}
          onCancelar={() => setBorrador(null)} />
      </div>
    );
  }

  // ------------------------------------------------------------- REPORTE -----
  return (
    <div className="space-y-4 p-6">
      {dlg.ui}
      <div className="flex items-center gap-2">
        <BarChart3 className="h-5 w-5 text-primary" />
        <h1 className="text-xl font-semibold">Inteligencia de negocios</h1>
      </div>

      <Card>
        <CardContent className="flex flex-wrap items-end gap-3 pt-6">
          <label className="flex flex-col text-xs text-muted-foreground">Reporte
            <select className={`${field} mt-1 w-72`} value={actual?.id ?? ''}
              onChange={(e) => { setReporteId(e.target.value); setColsTipo(''); setColsParam(''); setVista('valores'); setFilaGrafico(null); }}>
              {reportes.length === 0 && <option value="">— sin reportes —</option>}
              {reportes.map((r) => (
                <option key={r.id} value={r.id}>{r.es_plantilla ? '★ ' : ''}{r.nombre}</option>
              ))}
            </select>
          </label>
          <label className="flex flex-col text-xs text-muted-foreground">Desde
            <input type="date" className={`${field} mt-1 w-40`} value={desde} onChange={(e) => setDesde(e.target.value)} />
          </label>
          <label className="flex flex-col text-xs text-muted-foreground">Hasta
            <input type="date" className={`${field} mt-1 w-40`} value={hasta} onChange={(e) => setHasta(e.target.value)} />
          </label>
          <label className="flex flex-col text-xs text-muted-foreground">Columnas
            <select className={`${field} mt-1 w-44`} value={tipoEfectivo} onChange={(e) => setColsTipo(e.target.value)}>
              {TIPOS_COL.map((t) => <option key={t.v} value={t.v}>{t.label}</option>)}
            </select>
          </label>
          {tipoEfectivo === 'tiempo' && (
            <label className="flex flex-col text-xs text-muted-foreground">Período
              <select className={`${field} mt-1 w-44`} value={paramEfectivo} onChange={(e) => setColsParam(e.target.value)}>
                {PERIODOS.map((p) => <option key={p.v} value={p.v}>{p.label}</option>)}
              </select>
            </label>
          )}
          <label className="flex flex-col text-xs text-muted-foreground">Vista
            <select className={`${field} mt-1 w-56`} value={vista} onChange={(e) => setVista(e.target.value as Vista)}>
              <option value="valores">Valores</option>
              <option value="vertical" disabled={!filaBase}>
                % vertical{filaBase ? ` sobre ${filaBase.etiqueta}` : ' (define la fila base en Diseño)'}
              </option>
              <option value="variacion" disabled={tipoEfectivo !== 'tiempo'}>Variación vs. período anterior</option>
              <option value="presupuesto" disabled={tipoEfectivo !== 'tiempo'}>Real vs. Presupuesto</option>
            </select>
          </label>
          <label className="flex flex-col text-xs text-muted-foreground">Escala
            <select className={`${field} mt-1 w-32`} value={escala}
              onChange={(e) => setEscala(Number(e.target.value))}>
              {ESCALAS.map((x) => <option key={x.v} value={x.v}>{x.label}</option>)}
            </select>
          </label>
          {tipoEfectivo === 'tiempo' && centros.length > 0 &&
            <DimSelect label="Centro de costo" lista={centros} value={centro} onChange={setCentro} />}
          {tipoEfectivo === 'tiempo' && proyectos.length > 0 &&
            <DimSelect label="Proyecto" lista={proyectos} value={proyecto} onChange={setProyecto} />}
          {vistaEfectiva === 'valores' && (
            <label className="flex items-center gap-2 pb-2 text-xs text-muted-foreground">
              <input type="checkbox" checked={conTotal} onChange={(e) => setConTotal(e.target.checked)} />
              Columna Total
            </label>
          )}
          {esPresu && (
            <div className="flex flex-col pb-1 text-xs text-muted-foreground">
              <span>Por período</span>
              <div className="mt-1 flex items-center gap-2">
                {SUBS_PRESU.map((x) => (
                  <label key={x.k} className="flex items-center gap-1" title={x.ayuda}>
                    <input type="checkbox" checked={subPresu[x.k]}
                      onChange={(e) => setSubPresu((v) => ({ ...v, [x.k]: e.target.checked }))} />
                    {x.label}
                  </label>
                ))}
              </div>
            </div>
          )}
          <div className="ml-auto flex items-end gap-2 pb-1">
            {!cargandoLista && reportes.filter((r) => r.es_plantilla).length < 3 && (
              <Button size="sm" variant="outline" disabled={sembrar.isPending} onClick={() => sembrar.mutate()}
                title="Vuelve a crear las plantillas predefinidas que falten">
                <RefreshCw className="mr-1 h-4 w-4" /> Cargar plantillas
              </Button>
            )}
            <ExportarBotones
              nombre={`bi-${(actual?.nombre ?? 'reporte').toLowerCase().replace(/[^a-z0-9]+/g, '-')}-${desde}-${hasta}`}
              titulo={actual?.nombre ?? 'Reporte'}
              subtitulo={subtitulo}
              headers={headers} filas={filasExport} colsDerecha={colsDerecha} marca={marca}
              fmtPDF={(v, c) => {
                if (c === 0) return String(v ?? '');
                if (v == null) return '';
                // en la vista de presupuesto las columnas van en bloques de
                // `subs`; el % puede caer en cualquier posición del bloque
                const esPct = vistaEfectiva === 'vertical'
                  || (esPresu && !!subs[(c - 1) % subs.length]?.esPct);
                if (esPct) return `${Number(v).toFixed(1)} %`;
                // las filas ya vienen escaladas; con escala se omite el símbolo
                return escala === 1 ? usd(Number(v)) : numFmt(Number(v));
              }}
              disabled={isFetching}
            />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="flex flex-wrap items-center gap-2 text-base">
            <span>{actual?.nombre ?? 'Sin reporte'}</span>
            {actual?.es_plantilla && (
              <span className="rounded bg-muted px-1.5 py-0.5 text-[11px] font-normal text-muted-foreground">plantilla</span>
            )}
            {actual?.descripcion && (
              <span className="text-xs font-normal text-muted-foreground">{actual.descripcion}</span>
            )}
            {notaEscala && (
              <span className="rounded bg-primary/10 px-1.5 py-0.5 text-[11px] font-medium text-primary">
                {notaEscala}
              </span>
            )}
            <div className="ml-auto flex items-center gap-1">
              <Button size="sm" variant="outline" onClick={() => setGrafico((g) => !g)} disabled={cols.length < 2}>
                {grafico ? <ChevronUp className="mr-1 h-4 w-4" /> : <ChevronDown className="mr-1 h-4 w-4" />} Gráfico
              </Button>
              <Button size="sm" variant="outline" onClick={() => setBorrador(borradorVacio())}>
                <Plus className="mr-1 h-4 w-4" /> Nuevo
              </Button>
              <Button size="sm" variant="outline" disabled={!actual || clonar.isPending}
                onClick={() => actual && clonar.mutate(actual.id)}>
                <Copy className="mr-1 h-4 w-4" /> Clonar
              </Button>
              <Button size="sm" variant="outline"
                disabled={!actual || actual.es_plantilla || abrirEditor.isPending}
                title={actual?.es_plantilla ? 'Las plantillas no se editan: clónala y edita la copia' : 'Editar'}
                onClick={() => actual && abrirEditor.mutate(actual.id)}>
                <Pencil className="mr-1 h-4 w-4" /> Editar
              </Button>
              <Button size="sm" variant="outline" className="text-destructive"
                disabled={!actual || eliminar.isPending}
                onClick={async () => {
                  if (!actual) return;
                  if (await dlg.confirmar(`¿Eliminar el reporte "${actual.nombre}"?`, { peligro: true })) eliminar.mutate(actual.id);
                }}>
                <Trash2 className="mr-1 h-4 w-4" /> Eliminar
              </Button>
            </div>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {isFetching && <div className="py-8 text-center"><Loader2 className="mx-auto h-6 w-6 animate-spin text-primary" /></div>}
          {!!error && <p className="py-6 text-center text-sm text-destructive">{getErrorMessage(error)}</p>}
          {!isFetching && !error && filas.length === 0 && (
            <p className="py-8 text-center text-sm text-muted-foreground">
              {reportes.length === 0
                ? 'Todavía no hay reportes. Usa “Cargar plantillas” para empezar con las tres predefinidas, o “Nuevo” para armar el tuyo.'
                : 'Sin movimientos en el rango seleccionado.'}
            </p>
          )}

          {/* ------------------------------ Real vs. Presupuesto ------------- */}
          {!isFetching && !error && filas.length > 0 && esPresu && !hayPresupuesto && (
            <div className="mb-3 rounded-md bg-amber-500/10 px-3 py-2 text-sm text-amber-700">
              No hay presupuesto cargado para este período, así que todo aparece contra cero.{' '}
              <button className="underline decoration-dotted underline-offset-2"
                onClick={() => openTab({ id: '/contabilidad/presupuesto', title: 'Presupuesto', path: '/contabilidad/presupuesto' })}>
                Cárgalo en Analítico › Presupuesto
              </button>.
            </div>
          )}
          {!isFetching && !error && filas.length > 0 && esPresu && (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  {/* dos niveles: el período manda, las subcolumnas cuelgan de él */}
                  <tr className="text-xs text-muted-foreground">
                    <th rowSpan={2} className="sticky left-0 z-10 bg-card py-2 pr-4 text-left align-bottom">Concepto</th>
                    {cols.map((c) => (
                      <th key={c.clave} colSpan={subs.length}
                        className="whitespace-nowrap border-b border-l px-3 py-1 text-center font-medium">
                        {c.titulo}
                      </th>
                    ))}
                    <th colSpan={subs.length}
                      className="whitespace-nowrap border-b border-l bg-muted/40 px-3 py-1 text-center font-medium">
                      Todo el rango
                    </th>
                  </tr>
                  <tr className="border-b text-[11px] text-muted-foreground">
                    {[...cols.map((c) => c.clave), '__total'].map((clave, bloque) => (
                      subs.map((x) => (
                        <th key={`${clave}-${x.k}`}
                          className={`whitespace-nowrap px-3 py-1 text-right font-normal ${x.k === subs[0].k ? 'border-l' : ''} ${bloque === cols.length ? 'bg-muted/40' : ''}`}>
                          {x.label}
                        </th>
                      ))
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filas.map((f) => {
                    if (f.clase === 'separador') {
                      return <tr key={f.orden}><td colSpan={1 + (cols.length + 1) * subs.length} className="h-3" /></tr>;
                    }
                    const sinPpto = f.presupuesto == null;
                    return (
                      <tr key={f.orden} className={claseFila(f)}>
                        <td className="sticky left-0 z-10 whitespace-nowrap bg-inherit py-1.5 pr-4"
                          style={{ paddingLeft: `${f.nivel * 16}px` }}>
                          {f.etiqueta}
                          {sinPpto && <span className="ml-1 text-[10px] text-muted-foreground">(sin presupuesto)</span>}
                        </td>
                        {[...cols.map((_, i) => i), null].map((i, bloque) => {
                          const c = celdasPresu(f, i);
                          return subs.map((x) => {
                            const v = c[x.k];
                            return (
                              <td key={`${bloque}-${x.k}`}
                                className={`whitespace-nowrap px-3 py-1.5 text-right tabular-nums
                                  ${x.k === subs[0].k ? 'border-l' : ''}
                                  ${i === null ? 'bg-muted/40' : ''}
                                  ${x.k === 'ppto' ? 'text-muted-foreground' : ''}
                                  ${v != null && v < 0 ? 'text-destructive' : ''}`}>
                                {v == null ? '—'
                                  : x.esPct ? `${v.toFixed(1)} %`
                                    : fmt(v, 'moneda', escala)}
                              </td>
                            );
                          });
                        })}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* ------------------------------ Matriz normal -------------------- */}
          {!isFetching && !error && filas.length > 0 && !esPresu && (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-xs text-muted-foreground">
                    <th className="sticky left-0 z-10 bg-card py-2 pr-4 text-left">Concepto</th>
                    {cols.map((c) => <th key={c.clave} className="whitespace-nowrap px-3 py-2 text-right">{c.titulo}</th>)}
                    {totalVisible && <th className="whitespace-nowrap border-l py-2 pl-3 text-right">Total</th>}
                  </tr>
                </thead>
                <tbody>
                  {filas.map((f) => {
                    if (f.clase === 'separador') {
                      return <tr key={f.orden}><td colSpan={cols.length + (totalVisible ? 2 : 1)} className="h-3" /></tr>;
                    }
                    const vals = valoresDe(f);
                    const tot = totalDe(f);
                    const esBase = vistaEfectiva === 'vertical' && f.orden === baseOrden;
                    return (
                      <tr key={f.orden} className={claseFila(f)}>
                        <td className="sticky left-0 z-10 whitespace-nowrap bg-inherit py-1.5 pr-4"
                          style={{ paddingLeft: `${f.nivel * 16}px` }}>
                          {f.etiqueta}
                          {esBase && <span className="ml-1 text-[10px] text-muted-foreground">(base)</span>}
                        </td>
                        {vals.map((v, i) => (
                          <td key={cols[i]?.clave ?? i}
                            className={`whitespace-nowrap px-3 py-1.5 text-right tabular-nums ${v != null && v < 0 ? 'text-destructive' : ''}`}>
                            {vistaEfectiva === 'valores' && v !== 0 && actual ? (
                              <button className="underline decoration-dotted underline-offset-2 hover:text-primary"
                                title="Ver de qué cuentas sale esta cifra"
                                onClick={() => setCelda({ filaOrden: f.orden, colClave: cols[i].clave })}>
                                {fmt(v, formatoDe(f), escala)}
                              </button>
                            ) : fmt(v, formatoDe(f), escala)}
                          </td>
                        ))}
                        {totalVisible && (
                          <td className={`whitespace-nowrap border-l py-1.5 pl-3 text-right tabular-nums ${tot < 0 ? 'text-destructive' : ''}`}>
                            {fmt(tot, f.formato, escala)}
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {grafico && cols.length > 1 && filaGraf && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="flex flex-wrap items-center gap-3 text-base">
              Evolución
              {notaEscala && <span className="text-xs font-normal text-muted-foreground">({notaEscala})</span>}
              <select className={`${field} w-72`} value={filaGraf.orden}
                onChange={(e) => setFilaGrafico(Number(e.target.value))}>
                {filas.filter((f) => f.clase !== 'separador').map((f) => (
                  <option key={f.orden} value={f.orden}>{f.etiqueta}</option>
                ))}
              </select>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                {esSaldo ? (
                  <LineChart data={datosGraf}>
                    <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                    <XAxis dataKey="periodo" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} width={80}
                      tickFormatter={(v: number) => v.toLocaleString(LOCALE, { maximumFractionDigits: 0 })} />
                    <Tooltip formatter={(v: number | string) => (escala === 1 ? usd(Number(v)) : numFmt(Number(v)))} />
                    <Line type="monotone" dataKey="valor" name="Saldo" stroke="hsl(var(--primary))" strokeWidth={2} dot={false} />
                  </LineChart>
                ) : (
                  <BarChart data={datosGraf}>
                    <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                    <XAxis dataKey="periodo" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} width={80}
                      tickFormatter={(v: number) => v.toLocaleString(LOCALE, { maximumFractionDigits: 0 })} />
                    <Tooltip formatter={(v: number | string) => (escala === 1 ? usd(Number(v)) : numFmt(Number(v)))} />
                    {matriz?.con_presupuesto && <Legend />}
                    <Bar dataKey="valor" name="Real" radius={[3, 3, 0, 0]}>
                      {datosGraf.map((d, i) => (
                        <Cell key={i} fill={d.valor < 0 ? 'hsl(var(--destructive))' : 'hsl(var(--primary))'} />
                      ))}
                    </Bar>
                    {matriz?.con_presupuesto && (
                      <Bar dataKey="presupuesto" name="Presupuesto" fill="hsl(var(--muted-foreground))"
                        opacity={0.45} radius={[3, 3, 0, 0]} />
                    )}
                  </BarChart>
                )}
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      )}

      <p className="text-xs text-muted-foreground">
        Las filas de medida <em>saldo</em> muestran el acumulado al cierre de cada columna; las de medida
        <em> flujo</em>, el movimiento del período. La columna Total suma en flujo y toma el último período
        en saldo. Los asientos de cierre de ejercicio no entran al flujo. Haz clic en cualquier cifra de la
        vista <em>Valores</em> para ver de qué cuentas sale.
      </p>

      {celda && actual && (
        <BIDetalleModal
          params={{
            reporte: actual.id, filaOrden: celda.filaOrden, colClave: celda.colClave,
            desde, hasta, colsTipo: tipoEfectivo, colsParam: paramEfectivo,
            centro: centro || null, proyecto: proyecto || null,
          }}
          notaEscala={escala === 1000 ? 'miles' : escala === 1000000 ? 'millones' : undefined}
          onCerrar={() => setCelda(null)} />
      )}
    </div>
  );
}
